import React, { useEffect, useState } from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    MessagesSquare,
    MessageCircle,
    ThumbsUp,
    ThumbsDown,
    Globe,
    Loader2,
    Download,
    Users,
    Ticket as TicketIcon,
    Sparkles,
} from "lucide-react";
import PageHeader from "@/components/admin/PageHeader";
import { adminApi, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const INTENT_COLORS = ["#7E2A6C", "#A56FA0", "#B98E1A", "#688566", "#8D6B94", "#C19834", "#9B7BBD"];

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">{label}</div>
            <Icon size={15} className="text-orchid-700/60" />
        </div>
        <div className="font-heading text-4xl text-orchid-700 leading-none">{value}</div>
        {sub != null && <div className="text-[11px] text-stone-500 mt-2">{sub}</div>}
    </div>
);

const RANGES = [
    { label: "7d", value: 7 },
    { label: "30d", value: 30 },
    { label: "90d", value: 90 },
    { label: "180d", value: 180 },
];

export default function AnalyticsChats() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        setLoading(true);
        adminApi
            .get(`/api/admin/analytics/chats?days=${days}`)
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, [days]);

    const exportCsv = async () => {
        setExporting(true);
        try {
            const token = localStorage.getItem("orchid_admin_token");
            const res = await fetch(
                `${API_BASE}/api/admin/analytics/chats.csv?days=${days}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `orchid_analytics_${days}d.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Analytics CSV downloaded");
        } catch (err) {
            toast.error("Export failed: " + err.message);
        } finally {
            setExporting(false);
        }
    };

    if (loading && !data) {
        return (
            <div className="p-10 text-stone-500 flex items-center gap-2">
                <Loader2 className="animate-spin" size={16} /> Loading analytics…
            </div>
        );
    }

    const funnelConv = data.funnel.conversations || 0;
    const funnelLeads = data.funnel.leads || 0;
    const funnelTickets = data.funnel.tickets || 0;
    const leadConv = funnelConv ? Math.round((funnelLeads / funnelConv) * 100) : 0;
    const ticketConv = funnelConv ? Math.round((funnelTickets / funnelConv) * 100) : 0;

    return (
        <div className="p-10 max-w-7xl" data-testid="page-analytics-chats">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <PageHeader
                    eyebrow="Analytics"
                    title="Chats"
                    description="Conversation, lead and ticket performance across the Orchid Concierge."
                />
                <div className="flex items-center gap-2">
                    <div className="inline-flex border border-stone-200 rounded-md bg-white p-0.5">
                        {RANGES.map((r) => (
                            <button
                                key={r.value}
                                onClick={() => setDays(r.value)}
                                data-testid={`range-${r.value}`}
                                className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                    days === r.value
                                        ? "bg-orchid-700 text-white"
                                        : "text-stone-600 hover:bg-stone-50"
                                }`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={exportCsv}
                        disabled={exporting}
                        data-testid="analytics-export"
                        className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-orchid-700 text-orchid-700 hover:bg-orchid-700 hover:text-white disabled:opacity-50"
                    >
                        {exporting ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <Download size={13} />
                        )}
                        Export CSV
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Kpi
                    icon={MessagesSquare}
                    label="Total conversations"
                    value={data.total_conversations}
                    sub={`${funnelConv} in last ${days}d`}
                    testid="kpi-total-conversations"
                />
                <Kpi
                    icon={MessageCircle}
                    label="Total messages"
                    value={data.total_messages}
                    testid="kpi-total-messages"
                />
                <Kpi
                    icon={Users}
                    label="Leads captured"
                    value={funnelLeads}
                    sub={`${leadConv}% conversion`}
                    testid="kpi-leads"
                />
                <Kpi
                    icon={TicketIcon}
                    label="Tickets opened"
                    value={funnelTickets}
                    sub={`${ticketConv}% of conversations`}
                    testid="kpi-tickets"
                />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Kpi
                    icon={ThumbsUp}
                    label="Thumbs up"
                    value={data.thumbs_up}
                    testid="kpi-thumbs-up"
                />
                <Kpi
                    icon={ThumbsDown}
                    label="Thumbs down"
                    value={data.thumbs_down}
                    testid="kpi-thumbs-down"
                />
                <Kpi
                    icon={Sparkles}
                    label="Top intent"
                    value={data.intent_series[0]?.intent?.replace("_", " ") || "—"}
                    sub={data.intent_series[0] ? `${data.intent_series[0].count} chats` : ""}
                    testid="kpi-top-intent"
                />
                <Kpi
                    icon={MessagesSquare}
                    label="Avg msgs / chat"
                    value={
                        data.total_conversations
                            ? (data.total_messages / data.total_conversations).toFixed(1)
                            : "0"
                    }
                    testid="kpi-avg-msgs"
                />
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <div className="mb-5">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Last {days} days
                    </div>
                    <h3 className="font-heading text-2xl text-orchid-700">Chats started</h3>
                </div>
                <div className="h-64" data-testid="chart-chats-started">
                    <ResponsiveContainer width="100%" height={256}>
                        <AreaChart data={data.daily_series}>
                            <defs>
                                <linearGradient id="cs" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#7E2A6C" stopOpacity={0.3} />
                                    <stop offset="100%" stopColor="#7E2A6C" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tick={{ fill: "#78716c", fontSize: 11 }}
                                tickFormatter={(d) => (d ? d.slice(5) : "")}
                            />
                            <YAxis tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                            <Tooltip />
                            <Area
                                type="monotone"
                                dataKey="conversations"
                                stroke="#7E2A6C"
                                strokeWidth={2}
                                fill="url(#cs)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <div className="mb-4">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                            When are guests chatting
                        </div>
                        <h3 className="font-heading text-xl text-orchid-700">By hour of day</h3>
                    </div>
                    <div className="h-56" data-testid="chart-hourly">
                        <ResponsiveContainer width="100%" height={224}>
                            <BarChart data={data.hourly_series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                                <XAxis
                                    dataKey="hour"
                                    tick={{ fill: "#78716c", fontSize: 10 }}
                                    tickFormatter={(h) => `${h}h`}
                                    interval={2}
                                />
                                <YAxis tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                                <Tooltip
                                    formatter={(value) => [value, "chats"]}
                                    labelFormatter={(h) => `Hour ${h}`}
                                />
                                <Bar dataKey="count" fill="#7E2A6C" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <div className="mb-4">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                            Why are guests reaching out
                        </div>
                        <h3 className="font-heading text-xl text-orchid-700">Top intents</h3>
                    </div>
                    {data.intent_series.length === 0 ? (
                        <div className="text-sm text-stone-500 py-12 text-center">
                            No tagged intents yet in this window.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 items-center">
                            <div className="h-52" data-testid="chart-intents">
                                <ResponsiveContainer width="100%" height={208}>
                                    <PieChart>
                                        <Pie
                                            data={data.intent_series}
                                            dataKey="count"
                                            nameKey="intent"
                                            innerRadius={48}
                                            outerRadius={80}
                                            paddingAngle={2}
                                        >
                                            {data.intent_series.map((_, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={INTENT_COLORS[i % INTENT_COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value) => [value, "chats"]}
                                            labelFormatter={(l) => l}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <ul className="space-y-1.5 text-sm" data-testid="intent-legend">
                                {data.intent_series.slice(0, 7).map((i, idx) => (
                                    <li key={i.intent} className="flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-2 min-w-0">
                                            <span
                                                className="w-2.5 h-2.5 rounded-sm shrink-0"
                                                style={{ background: INTENT_COLORS[idx % INTENT_COLORS.length] }}
                                            />
                                            <span className="truncate text-stone-700">
                                                {i.intent.replace("_", " ")}
                                            </span>
                                        </span>
                                        <span className="text-stone-900 font-medium">{i.count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Conversion funnel */}
            <div className="bg-white border border-stone-200 rounded-lg p-6 mb-6">
                <div className="mb-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Conversion funnel (last {days}d)
                    </div>
                    <h3 className="font-heading text-xl text-orchid-700">Browsing → Lead → Ticket</h3>
                </div>
                <div className="space-y-3" data-testid="funnel">
                    {[
                        { label: "Conversations", value: funnelConv, pct: 100, color: "#7E2A6C" },
                        {
                            label: "Leads captured",
                            value: funnelLeads,
                            pct: funnelConv ? Math.round((funnelLeads / funnelConv) * 100) : 0,
                            color: "#B98E1A",
                        },
                        {
                            label: "Tickets opened",
                            value: funnelTickets,
                            pct: funnelConv ? Math.round((funnelTickets / funnelConv) * 100) : 0,
                            color: "#688566",
                        },
                    ].map((step) => (
                        <div key={step.label}>
                            <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-stone-700 font-medium">{step.label}</span>
                                <span className="text-stone-900">
                                    {step.value}{" "}
                                    <span className="text-xs text-stone-500">({step.pct}%)</span>
                                </span>
                            </div>
                            <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{ width: `${Math.max(step.pct, 2)}%`, background: step.color }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <Users size={16} className="text-orchid-700" />
                        <h3 className="font-heading text-xl text-orchid-700">Leads by type</h3>
                    </div>
                    {data.leads_by_type?.length ? (
                        <ul className="space-y-3" data-testid="list-leads-types">
                            {data.leads_by_type.map((l) => (
                                <li key={l.type} className="flex items-center justify-between">
                                    <span className="text-sm text-stone-700 capitalize">{l.type}</span>
                                    <span className="text-sm font-medium text-orchid-700">{l.count}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-stone-500">No leads captured in this window yet.</p>
                    )}
                </div>

                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <Globe size={16} className="text-orchid-700" />
                        <h3 className="font-heading text-xl text-orchid-700">Chats by country</h3>
                    </div>
                    {data.by_country?.length ? (
                        <ul className="space-y-3" data-testid="list-by-country">
                            {data.by_country.map((c) => (
                                <li key={c.country} className="flex items-center justify-between">
                                    <span className="text-sm text-stone-700">{c.country}</span>
                                    <span className="text-sm font-medium text-orchid-700">{c.count}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-stone-500">
                            Country data will appear once IP geolocation is enabled.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
